import { defineStore } from 'pinia';
import { refreshTokens } from '@/api/authRefresh';
import { vitrineMeApi } from '@/api/vitrineCard';
import { authApi } from '@/api/auth';
import { devicePinApi } from '@/api/devicePin';
import { disablePasskey } from '@/api/webauthn';
import { clearSupportAttachmentBlobs } from '@/api/support';
import { currentProduct, setProduct, productFromAccessToken } from '@/product';
import type { Locale } from '@/i18n';
import { forgetHandoffToken } from '@/magicHandoff';

interface AuthState {
  accessToken: string | null;
  // refreshToken is NO LONGER stored/used — it lives in an HttpOnly cookie (XSS can't read it).
  // Field kept only so legacy dual-write responses can be assigned without type churn.
  refreshToken: string | null;
  customerId: string | null;
  sessionId: string | null;
  preferredLanguage: Locale | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    accessToken: null,
    refreshToken: null,
    customerId: null,
    sessionId: null,
    preferredLanguage: null,
  }),
  getters: {
    isAuthenticated: state => state.accessToken !== null,
  },
  actions: {
    setTokens(tokens: { accessToken: string; refreshToken?: string; customerId: string; sessionId: string }) {
      this.accessToken = tokens.accessToken;   // in-memory only (not persisted)
      this.customerId = tokens.customerId;
      this.sessionId = tokens.sessionId;
      // Resolve product from the token's ctype claim (SHOWCASE→vitrine, FULL→full) + persist the hint
      // so a cold-boot refresh picks the right (path-scoped) endpoint.
      const p = productFromAccessToken(tokens.accessToken);
      if (p) setProduct(p);
      // refreshToken intentionally ignored — the HttpOnly cookie is the source of truth.
    },

    setPreferredLanguage(lang: Locale) {
      this.preferredLanguage = lang;
      // Tell the backend too: it renders the emails and pushes, and a choice kept only in localStorage leaves
      // every notification English. Lazy import — a static one closes the auth → api → http → auth cycle and
      // breaks app boot. Best-effort: a failed sync must never block switching the UI language, and the next
      // switch retries anyway.
      if (this.customerId) {
        void import('@/api/vitrineCard')
          .then(m => m.vitrineCardApi.setLanguage(lang))
          .catch(() => { /* offline or logged out — the UI language still switched */ });
      }
    },

    // Silent refresh: the refresh token rides in the HttpOnly cookie (withCredentials), no arg needed.
    async refreshAccessToken(): Promise<string> {
      const t = await refreshTokens();
      this.setTokens(t);
      return t.accessToken;
    },

    /**
     * Boot-time restore: access token is in-memory only, so a reload loses it. If we have a
     * persisted session hint (customerId), try a silent refresh from the cookie. On failure →
     * treated as logged-out. Must run before the router guards evaluate isAuthenticated.
     */
    async initializeAuth(): Promise<void> {
      if (this.accessToken) return;
      if (!this.customerId || !currentProduct()) {
        await this.adoptCookieSession();
        return;
      }
      try {
        await this.refreshAccessToken();
      } catch {
        this.accessToken = null;
        this.customerId = null;
        this.sessionId = null;
      }
    },

    /**
     * No session hint in this storage, but the refresh cookie may still be here. Adding the app to the iPhone Home
     * Screen copies Safari's cookies into the installed app and NOT its localStorage, so the app opened by the
     * client who had just signed in by the magic link found no hint, never tried the cookie, and asked for an
     * e-mail code again (owner, 03.10). One silent call settles it: success gives this app a session (and stores
     * the hint), a 401 just leaves the visitor anonymous. Vitrine only — it is the product the phone app is.
     *
     * It ADOPTS rather than refreshes (owner, 04.10): the copy and Safari's original are the same token, and
     * rotating it here made Safari's copy look stolen the next time a link opened there — reuse detection then
     * signed the client out of this app. Adoption gives the app its own session chain and leaves Safari's alone.
     */
    async adoptCookieSession(): Promise<void> {
      try {
        const { vitrineAuthApi } = await import('@/api/vitrineAuth');   // lazy: a static import closes a cycle
        const t = await vitrineAuthApi.adopt();
        if (t && !t.error && t.accessToken) {
          this.setTokens({ accessToken: t.accessToken, customerId: t.customerId, sessionId: t.sessionId });
        }
      } catch { /* no cookie, or it is no longer valid — stay anonymous */ }
    },

    async logout() {
      // An explicit "Sign out" must truly end access on this device: drop EVERY quick-return path, or
      // the login screen still offers Face ID / PIN and lets anyone straight back in without the magic
      // link/password. Three things remember this device — the device PIN, the passkey (Face ID), and
      // the last-email hint the Face ID button keys off — so clear all three FOR THIS DEVICE. Both server
      // calls are per-device (V083): clearPin and disablePasskey resolve the tacita_device cookie and
      // revoke only THIS device's credential — signing out here never touches other devices. Both need
      // the live session, so they run BEFORE the revoke below.
      try { await devicePinApi.clearPin(); } catch { /* no PIN, or server down */ }
      try { await disablePasskey(); } catch { /* no passkey, or server down */ }
      try { localStorage.removeItem('vitrine-last-email'); } catch { /* private mode */ }
      forgetHandoffToken();   // a signed-out tab must not keep a sign-in link in its address
      // Server-side session revoke + refresh-cookie clear — product picks the rail's logout endpoint.
      try {
        if (currentProduct() === 'vitrine') await vitrineMeApi.logout();
        else await authApi.logout();
      } catch {
        /* server may be down — proceed with local clear */
      }
      this.accessToken = null;
      this.refreshToken = null;
      this.customerId = null;
      this.sessionId = null;
      // Attachments and managers' photos fetched for this client stay in memory as object URLs otherwise.
      clearSupportAttachmentBlobs();
      // Drop the cached onboarding stage: on a shared device the next client's first paint would otherwise use
      // the previous client's stage for the tab bar and the route guard.
      // Imported lazily on purpose — the onboarding store reaches the API layer, which imports THIS store, and a
      // static import closes that cycle at module-init time and breaks the whole app boot.
      void import('@/stores/onboarding').then(m => m.useOnboardingStore().reset()).catch(() => { /* noop */ });
      // Product hint intentionally KEPT — it's the "last used product" default for the login screen
      // (a re-login re-confirms it from the fresh token). Cleared session (no customerId) already
      // prevents any auto-refresh, so a stale hint is harmless.
      // preferredLanguage intentionally kept — UX preference survives logout
    },
  },
  persist: {
    storage: localStorage,
    // Tokens are NOT persisted: access = in-memory, refresh = HttpOnly cookie. Only a session hint
    // (customerId/sessionId) + UX prefs survive reload → drives the boot-time silent refresh.
    paths: ['customerId', 'sessionId', 'preferredLanguage'],
  },
});
